import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NgModel} from '@angular/forms';
import concat from 'lodash-es/concat';
import isNil from 'lodash-es/isNil';
import {takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {GPTranslateService} from '../../localization/translate.service';

export class TreeItem {

  public text: string;
  public value: any;
  public transIds: string[];
  public collapsed: boolean = true;
  public children?: TreeItem[];
  public items?: any[];
  public data?: any;
  public flagImage?: any;
  public tooltip?: string;
  public canExpandWhenDisabled = false;

  private internalDisabled: boolean = false;
  private internalChecked: boolean = false;

  constructor(text: string, value?: any, transIds: string[] = []) {
    this.text = text;
    this.value = value;
    this.transIds = transIds;
  }

  public get checked(): boolean {
    return this.internalChecked;
  }

  public set checked(checked: boolean) {
    if (!this.disabled) {
      this.internalChecked = checked;
    }
  }

  public get disabled(): boolean {
    return this.internalDisabled;
  }

  public set disabled(disabled: boolean) {
    this.internalDisabled = disabled;
    if (!isNil(this.children)) {
      this.children.forEach((child: any) => child.disabled = disabled);
    }
  }

  public updateCollapsedRecursive(collapsed: boolean) {
    this.collapsed = collapsed;
    if (!isNil(this.children)) {
      this.children.forEach(child => {
        child.updateCollapsedRecursive(collapsed);
      });
    }
  }

  public updateCheckedRecursive(checked: boolean) {
    if (this.disabled && !this.canExpandWhenDisabled) {
      return;
    }

    this.checked = checked;

    if (!isNil(this.children)) {
      this.children.forEach(child => {
        child.updateCheckedRecursive(checked);
      });
    }
  }

  public getCheckedItems(): TreeItem[] {
    let checkedItems: TreeItem[] = [];
    if (isNil(this.children)) {
      if (this.checked) {
        checkedItems.push(this);
      }
    } else {
      this.children.forEach(child => {
        checkedItems = concat(checkedItems, child.getCheckedItems());
      });
    }

    return checkedItems;
  }
}

@Component({
  selector: 'gp-treeview',
  template: `
    <div class="treeview-item p-r" [class.treeview-parent]="item.children">
      <i *ngIf="item.children" (click)="toggleCollapseExpand()" aria-hidden="true"
         class="fa fa-light"
         [ngClass]="{'fa-caret-right': item.collapsed,
                     'fa-caret-down': !item.collapsed,
                     'disabled': item.disabled && !item.canExpandWhenDisabled}"></i>
      <label class="form-check-label p-r checkbox" [tooltip]="item.tooltip" container="body"
             [adaptivePosition]="false">
        <input type="checkbox" class="form-check-input no-margin"
               [(ngModel)]="item.checked" (ngModelChange)="onCheckedChange($event)" [disabled]="item.disabled"/>
        <img *ngIf="!item.children && item.flagImage" class="flag-icon m-l" src="/repository/api/v1/cliReg/703/flag"/>
        {{ item.text ? (item.text | translate) : transIdsDisplay(item.transIds) }}
      </label>
      <div *ngFor="let child of item.children">
        <gp-treeview
          *ngIf="!item.collapsed"
          [item]="child"
          [transIdSeperator]="transIdSeperator"
          [checkParentOnAnyChild]="checkParentOnAnyChild"
          (checkedChange)="onChildCheckedChange($event)">
        </gp-treeview>
      </div>
    </div>
  `,
  styleUrls: ['./treeview.component.css']
})
export class TreeviewComponent extends Destroyable implements OnInit {

  @Input() public item: TreeItem;
  @Input() public transIdSeperator;
  @Input() public checkParentOnAnyChild: boolean = false;
  @Output() public checkedChange: EventEmitter<any> = new EventEmitter<boolean>();
  @ViewChild(NgModel) private checkboxModel: NgModel;

  constructor(private gpTranslateService: GPTranslateService) {
    super();
  }

  public ngOnInit(): void {
    if (this.item.children) {
      this.childrenSort();
    }

    this.gpTranslateService.onLangChange.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      if (this.item.children) {
        this.childrenSort();
      }
    });
  }

  public toggleCollapseExpand() {
    if (!this.item.disabled || this.item.canExpandWhenDisabled) {
      this.item.collapsed = !this.item.collapsed;
    }
  }

  public childrenSort() {
    this.item.children.sort((a: any, b: any) => {
      if (!a.children && b.children) {
        return -1;
      }
      if (a.children && !b.children) {
        return 1;
      }
      if (a.text && b.text) {
        if (this.gpTranslateService.instant(a.text) < this.gpTranslateService.instant(b.text)) {
          return -1;
        } else {
          return 1;
        }
      } else {
        if (this.transIdsDisplay(a.transIds) < this.transIdsDisplay(b.transIds)) {
          return -1;
        } else {
          return 1;
        }
      }
    });
  }

  public onCheckedChange(checked: boolean) {
    if (!isNil(this.item.children)) {
      this.item.children.forEach(child => {
        child.updateCheckedRecursive(checked);
      });
    }

    this.checkedChange.emit(checked);
    this.syncCheckboxWithItem();
  }

  public onChildCheckedChange(checked: boolean) {
    this.item.checked = this.checkParentOnAnyChild
      ? this.item.children.some(child => child.checked)
      : this.item.children.every(child => child.checked);
    this.checkedChange.emit(this.item.checked);
  }

  public transIdsDisplay(transIds: string[]): string {
    return this.gpTranslateService.getDisplayTranslation(transIds, this.transIdSeperator);
  }

  /**
   * A consumer may restore `item.checked` while handling the click (e.g. a locked child keeps its parent available).
   * The ngModel binding then sees no change and would leave the clicked checkbox showing the user's input.
   */
  private syncCheckboxWithItem(): void {
    if (this.checkboxModel && this.checkboxModel.value !== this.item.checked) {
      this.checkboxModel.control.setValue(this.item.checked, {emitViewToModelChange: false});
    }
  }
}
